import { type Component, untrack } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import InputModal from "../../../components/modal/InputModal";
import { object, pipe } from "valibot";
import { errorTranslation, normalizedLiteral, requireText } from "../../../utils/validation/base";
import { Translation } from "../../../utils/Translation";
import { TextInput } from "../../../components/inputs/TextInput";
import { MAX_CHARACTERS_MD } from "../../../constants";
import ApiService from "../../../services/ApiService";
import type FormId from "../../../state/FormId";
import { useNavigate } from "@solidjs/router";
import type FolderId from "../../../state/FolderId";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import type CustomerId from "../../../state/CustomerId";
import { keyFromSelector } from "i18next";

type DeleteFormModalProps = {
  customerId: CustomerId;
  folderId: FolderId;
  formId: FormId;
  formName: string;
};

const deleteFormSchema = (formName: string) =>
  object({
    name: pipe(
      requireText(MAX_CHARACTERS_MD),
      normalizedLiteral(
        formName,
        errorTranslation(keyFromSelector(($) => $.DELETE_FORM_MODAL_INPUT_NAME_VALIDATION_NOT_EQUAL)),
      ),
    ),
  });

const DeleteFormModal: Component<DeleteFormModalProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { replace } = useModalManager();

  return (
    <InputModal
      variant="destructive"
      title={t(($) => $.DELETE_FORM_MODAL_TITLE)}
      confirmLabel={t(($) => $.DELETE_FORM_MODAL_ACTIONS_DELETE)}
      cancelLabel={t(($) => $.DELETE_FORM_MODAL_ACTIONS_CANCEL)}
      onConfirm={() =>
        new Promise((resolve, reject) => {
          ApiService.deleteForm().mutate(
            untrack(() => ({ folderId: props.folderId, formId: props.formId })),
            {
              onSuccess: () => {
                navigate(`/customers/${props.customerId}/folders/${props.folderId}`);
                resolve();
              },
              onError: (err) => {
                replace({
                  content: () => (
                    <GenericFailureModal title={t(($) => $.DELETE_FORM_FAILURE_MODAL_TITLE)} error={err} />
                  ),
                });
                reject(err);
              },
            },
          );
        })
      }
      schema={deleteFormSchema(props.formName)}
    >
      {({ value, updateValue, error }) => (
        <div class="flex flex-col gap-6">
          <div class="text-base-regular wrap-break-word text-secondary">
            <Translation
              key={keyFromSelector(($) => $.DELETE_FORM_MODAL_DESCRIPTION)}
              options={{ formName: props.formName }}
              components={{
                b: (props) => <span class="text-base-semibold text-primary">{props.children}</span>,
              }}
            />
          </div>

          <TextInput
            label={t(($) => $.DELETE_FORM_MODAL_INPUT_NAME)}
            value={value("name")}
            required
            onBlur={(e) => {
              updateValue("name", e.target.value);
            }}
            onInput={(e) => {
              updateValue("name", e.target.value);
            }}
            errorMessage={error("name")}
          />
        </div>
      )}
    </InputModal>
  );
};

export default DeleteFormModal;
